import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { DECISION_ENGINE_API_KEY } from './api';

const AUTH_STORAGE_KEY = 'de_authenticated';

interface AuthContextValue {
	isAuthenticated: boolean;
	/**
	 * Validates the provided key against REACT_APP_DECISION_ENGINE_API_KEY.
	 * Returns true and persists the authenticated state on success.
	 */
	login: (key: string) => boolean;
	logout: () => void;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

function readInitialAuth(): boolean {
	try {
		return sessionStorage.getItem(AUTH_STORAGE_KEY) === 'true';
	} catch {
		return false;
	}
}

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
	const [isAuthenticated, setIsAuthenticated] = useState<boolean>(readInitialAuth);

	const login = useCallback((key: string): boolean => {
		const trimmed = key.trim();
		// If no key is configured on the environment, deny access rather than
		// silently letting an empty key through.
		if (!DECISION_ENGINE_API_KEY) return false;
		if (trimmed !== DECISION_ENGINE_API_KEY) return false;

		try {
			sessionStorage.setItem(AUTH_STORAGE_KEY, 'true');
		} catch {
			/* ignore storage failures; auth still holds for this session in memory */
		}
		setIsAuthenticated(true);
		return true;
	}, []);

	const logout = useCallback(() => {
		try {
			sessionStorage.removeItem(AUTH_STORAGE_KEY);
		} catch {
			/* ignore */
		}
		setIsAuthenticated(false);
	}, []);

	const value = useMemo<AuthContextValue>(
		() => ({ isAuthenticated, login, logout }),
		[isAuthenticated, login, logout]
	);

	return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export function useAuth(): AuthContextValue {
	const ctx = useContext(AuthContext);
	if (!ctx) {
		throw new Error('useAuth must be used within an AuthProvider');
	}
	return ctx;
}
